import { useState, type FormEvent, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { authApi } from "@/lib/authApi";

const AUTH_KEY = ["auth", "me"] as const;

function errorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return "Network error";
  if (err.status === 401) return "Wrong password";
  return `Login failed (HTTP ${String(err.status)})`;
}

export function LoginPage(): ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loginMut = useMutation({
    mutationFn: () => authApi.login({ password }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: AUTH_KEY });
      setError(null);
      void navigate("/dashboard", { replace: true });
    },
    onError: (err) => {
      setError(errorMessage(err));
    },
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (password.length === 0) return;
    loginMut.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-0 p-6">
      <div className="card w-full max-w-sm">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent-bg">
            <i className="ti ti-chart-line text-2xl text-accent" />
          </div>
          <h2 className="mb-1">Sign in</h2>
          <p className="text-xs text-ink-secondary">Trading bot - nss.srin-trade.com</p>
        </div>
        <LoginForm
          password={password}
          onPasswordChange={setPassword}
          onSubmit={submit}
          error={error}
          pending={loginMut.isPending}
        />
        <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-ink-muted">
          <i className="ti ti-lock text-sm" />
          Single-user bot. Cookie lasts 7 days.
        </div>
      </div>
    </div>
  );
}

interface FormProps {
  password: string;
  onPasswordChange: (next: string) => void;
  onSubmit: (e: FormEvent) => void;
  error: string | null;
  pending: boolean;
}

function LoginForm({
  password,
  onPasswordChange,
  onSubmit,
  error,
  pending,
}: Readonly<FormProps>): ReactElement {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <label className="block">
        <span className="label">Password</span>
        <input
          className="input"
          type="password"
          value={password}
          onChange={(e) => {
            onPasswordChange(e.target.value);
          }}
          autoFocus
          autoComplete="current-password"
        />
      </label>
      {error !== null && (
        <p role="alert" className="rounded-ctl bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      <button
        type="submit"
        className="btn btn-primary w-full"
        disabled={pending || password.length === 0}
      >
        {pending ? "Signing in..." : "Sign in"}
      </button>
    </form>
  );
}
